import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/*
  BottomSheet — the phone sheet that slides up from the bottom (drag the handle down or tap outside to close).
  On wider screens it is a centred sheet (max 560px). For "Scanned · MTN693003" item actions (18 #10), site mode
  checklists (09 #8), quick forms on site.

    <BottomSheet open={open} onOpenChange={setOpen}
      title="MTN693003" description="Power supply REG, 24 V DC / 0.4 A"
      headerAddon={<StatusPill status="In stock" domain="stock" />}
      footer={<ActionBar actions={[…]} />}>
      …stock per location…
    </BottomSheet>

  Props: open, onOpenChange, title (required — the sheet's accessible name; `hideTitle` keeps it for screen
  readers only), description, headerAddon (node next to the title), banner (node above the title, e.g. the
  green "Scanned · just now" strip), children (scrolls), footer (stays at the bottom, above the home indicator),
  dismissible (true), showClose (true), maxHeight ('88dvh'), dragAnywhere (false: only the handle drags the sheet,
  so lists scroll, inputs select text and a SignaturePad inside the sheet draws instead of closing it — true for
  short read-only sheets), className, bodyClassName, testId ('bottom-sheet').
*/
function BottomSheet({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  description,
  headerAddon,
  banner,
  children,
  footer,
  dismissible = true,
  showClose = true,
  maxHeight = "88dvh",
  dragAnywhere = false,
  className,
  bodyClassName,
  testId = "bottom-sheet",
}) {
  return (
    <DrawerPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      shouldScaleBackground={false}
      dismissible={dismissible}
      handleOnly={!dragAnywhere}
    >
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-[color:var(--scrim)]" />
        <DrawerPrimitive.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full flex-col rounded-t-[20px] border border-b-0 border-line bg-surface text-ink shadow-overlay outline-none sm:max-w-[560px]",
            className
          )}
          style={{ maxHeight }}
          data-testid={testId}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <DrawerPrimitive.Handle className="!mt-2.5 !h-1.5 !w-11 shrink-0 !bg-line !opacity-100" />
          {banner ? <div className="px-4 pt-3">{banner}</div> : null}
          <div className={cn("flex items-start gap-3 px-4 pb-2 pt-3", hideTitle && !description && !headerAddon && "pb-0 pt-1")}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <DrawerPrimitive.Title className={cn("font-heading text-[19px] font-bold leading-tight text-ink", hideTitle && "sr-only")}>
                  {title}
                </DrawerPrimitive.Title>
                {headerAddon}
              </div>
              {description ? (
                <DrawerPrimitive.Description className="mt-1 text-[14px] text-ink-2">{description}</DrawerPrimitive.Description>
              ) : null}
            </div>
            {showClose ? (
              <DrawerPrimitive.Close
                className="-mr-1 grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-surface-2 text-ink-2 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close"
                data-testid={`${testId}-close`}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </DrawerPrimitive.Close>
            ) : null}
          </div>
          <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4", bodyClassName)}>{children}</div>
          {footer ? (
            <div className="shrink-0 border-t border-line px-4 pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-3">{footer}</div>
          ) : (
            <div className="shrink-0 pb-[env(safe-area-inset-bottom,0px)]" aria-hidden="true" />
          )}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  )
}

export { BottomSheet }
